// @flow
import * as R from "ramda"
import { addDays, subDays, max as maxDate, addMonths } from "date-fns"
import { Map, List } from "immutable"

import SearchPlace from "common/records/SearchPlace"
import MultiPlace from "common/records/MultiPlace"
import { createAnytime, createDateRange, isDateRange } from "common/records/SearchDate/utils"
import Place from "common/records/Place"
import { PLACE_VALUE_MODES } from "common/consts/place"
import { DATE_TYPES } from "common/records/SearchDate/SearchDate"
import type { DateRange, SearchDate, Anytime } from "common/records/SearchDate/SearchDate"
import type SalesmanFormData from "common/records/SalesmanFormData"
import type { SearchFormState } from "common/records/SearchFormState.types"

export const ONBOARDING_COOKIE = "SALESMAN_ONBOARDING_SEEN"
export const MAX_SALESMAN_LINES = 12

const makeSalesmanCompatible = (place: SearchPlace, places: Map<string, Place>, via: ?boolean) => {
  switch (place.mode) {
    case PLACE_VALUE_MODES.RADIUS:
      return place.value
        .getPlacesInRadius(places)
        .slice(0, 1)
        .map(
          value =>
            new SearchPlace({
              mode: PLACE_VALUE_MODES.PLACE,
              value,
            }),
        )
        .toArray()
    case PLACE_VALUE_MODES.PLACE:
      if (place.isLarge() || (via && place.isSpecial())) return null
      return place
    default:
      return null
  }
}

const nextSalesmanDate = (lastDate: DateRange): DateRange => ({
  type: DATE_TYPES.DATE_RANGE,
  isDefault: false,
  from: maxDate(addDays(lastDate.to, -1), lastDate.from),
  to: addDays(lastDate.to, 5),
})

export const getSalesmanDefault = (
  multiplace: MultiPlace,
  places: Map<string, Place>,
  via: boolean = false,
) => {
  let values: Array<Place> = R.flatten(
    multiplace.places
      .toArray()
      .map(place => makeSalesmanCompatible(place, places, via))
      .filter(Boolean),
  )
  if (via) {
    values = values.slice(0, 1)
  }
  return multiplace.set("places", new List(values))
}

export const getSalesmanDefaultDate = (
  index: number,
  trips: List<SalesmanFormData>,
): SearchDate => {
  switch (index) {
    case 0:
      return createDateRange(new Date(), addMonths(new Date(), 1))
    case 1:
      return createAnytime(true)
    default:
      return nextSalesmanDate(trips.get(index === 2 ? 0 : index - 1).dateRange)
  }
}

export type AllDates = List<{|
  i: number,
  date: DateRange | Anytime,
|}>

export const getSalesmanAllDates = (searchForm: SearchFormState): AllDates =>
  searchForm
    .getIn(["salesman", "routes"])
    .filter(salesmanData => salesmanData.dateRangeEnabled)
    .map((trip, index) => ({ i: index, date: trip.dateRange }))

export const getSalesmanRouteMinDate = (i: number, allDates: AllDates) => {
  const outboundDate = allDates.get(0).date

  return i !== 0 && isDateRange(outboundDate) ? outboundDate.from : undefined
}

export const getSalesmanRouteMaxDate = (i: number, allDates: AllDates) => {
  const inboundDate = allDates.get(1).date

  return i > 1 && isDateRange(inboundDate) ? subDays(inboundDate.to, 1) : undefined
}
